import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Lock, Plus, RefreshCw, Search, ShieldAlert, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { IMPACT_LABELS, LEVEL_CELL, LEVEL_LABELS, LEVEL_TONES, PROBABILITY_LABELS, RISK_CATEGORIES, RISK_STATUS_LABELS, RISK_VIEW_TABS, canExportGovernance, canManageRisks, formatDay, governanceError, matrixCells } from "../lib/governance";
import { Chip } from "../components/governance/GovernanceBits";
import ExportButton from "../components/governance/ExportButton";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const EMPTY_TEXT = {
  open: ["Aucun risque ouvert", "Consignez ce qui pourrait nuire à l'entreprise : on sait alors qui en répond et quand on le regarde à nouveau."],
  closed: ["Aucun risque accepté ou clos", "Un risque accepté ou clos reste consultable avec son motif."],
  all: ["Aucun risque pour le moment", "Le registre des risques de l'entreprise se construit ici."],
};

function StateChips({ r, testid }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Chip label={LEVEL_LABELS[r.level]} tone={LEVEL_TONES[r.level]} testid={testid} />
      {r.status !== "open" && <Chip label={RISK_STATUS_LABELS[r.status]} tone="slate" />}
      {r.to_review && <Chip label="À revoir" tone="amber" testid={`${testid}-review`} />}
      {r.restricted && <Chip label="Restreint" tone="slate"><Lock size={11} aria-hidden="true" /></Chip>}
    </span>
  );
}

// Open risks by impact (rows) and probability (columns): a click keeps the risks of that cell. Counts only — no name leaves the list.
function RiskGrid({ matrix, picked, onPick }) {
  const cells = useMemo(() => matrixCells(matrix), [matrix]);
  return (
    <Card className="p-4" data-testid="risk-grid">
      <h2 className="text-sm font-semibold text-slate-900 mb-2">Risques ouverts par gravité</h2>
      <div className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-1 text-xs">
        <div />
        {[1, 2, 3, 4].map((p) => <div key={p} className="text-center text-slate-500 px-0.5">{PROBABILITY_LABELS[p]}</div>)}
        {[4, 3, 2, 1].map((impact) => (
          <div key={impact} className="contents">
            <div className="pr-2 text-right text-slate-500 self-center">{IMPACT_LABELS[impact]}</div>
            {cells.filter((c) => c.impact === impact).map((c) => {
              const on = picked && picked.impact === c.impact && picked.probability === c.probability;
              return (
                <button key={c.probability} type="button" disabled={!c.count} onClick={() => onPick(on ? null : c)} aria-pressed={on}
                  aria-label={`${IMPACT_LABELS[c.impact]}, ${PROBABILITY_LABELS[c.probability]} : ${c.count} risque${c.count > 1 ? "s" : ""}`} data-testid={`grid-${c.impact}-${c.probability}`}
                  className={`h-10 rounded font-semibold tabular-nums ${LEVEL_CELL[c.level]} ${c.count ? "" : "opacity-40"} ${on ? "ring-2 ring-jamora" : ""}`}>{c.count || ""}</button>
              );
            })}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500">Ligne = impact, colonne = probabilité. Le niveau se calcule : impact × probabilité.</p>
    </Card>
  );
}

export default function GovernanceRisks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = RISK_VIEW_TABS.some(([id]) => id === params.get("view")) ? params.get("view") : "open";
  const search = params.get("q") || "";
  const category = params.get("category") || "";
  const mine = params.get("mine") === "1";
  const toReview = params.get("review") === "1";
  const [searchInput, setSearchInput] = useState(search);
  const [cell, setCell] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, counts: {}, matrix: [] });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);
  const canManage = canManageRisks(user);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const id = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/governance/risks", { params: { view, limit: 200, ...(search ? { q: search } : {}), ...(category ? { category } : {}), ...(mine ? { owner: "me" } : {}) } });
      if (id !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (id !== seq.current) return;
      setError(governanceError(e, "Impossible de charger les risques."));
      setPhase("error");
    }
  }, [view, search, category, mine]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const items = data.items.filter((r) => (!toReview || r.to_review) && (!cell || (r.impact === cell.impact && r.probability === cell.probability && r.status === "open")));
  const filtered = !!(search || category || mine || toReview || cell);
  const reset = () => { setSearchInput(""); setCell(null); setParams({}, { replace: true }); };
  const open = (r) => navigate(`/gouvernance/risques/${r.id}`);

  return (
    <div className="space-y-4" data-testid="governance-risks-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Risques</h1>
          <p className="text-sm text-slate-500">Ce qui pourrait nuire à l'entreprise : sa gravité, qui en répond, ce qui est fait, et quand on le regarde à nouveau.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canExportGovernance(user) && <ExportButton kind="risks" />}
          {canManage && <Button asChild data-testid="risk-new"><Link to="/gouvernance/risques/nouveau"><Plus size={16} className="mr-1.5" />Nouveau risque</Link></Button>}
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="Risques">
        {RISK_VIEW_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => update({ view: id === "open" ? "" : id })} data-testid={`risks-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${view === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}{data.counts?.[id] != null && <span className={`ml-1.5 tabular-nums ${view === id ? "text-white/80" : "text-slate-400"}`} data-testid={`risks-count-${id}`}>{data.counts[id]}</span>}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou titre…" className="pl-9 h-9" aria-label="Rechercher un risque" data-testid="risks-search" />
        </div>
        <Select value={category || ALL} onValueChange={(v) => update({ category: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-56" aria-label="Catégorie" data-testid="risks-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Toutes les catégories</SelectItem>{RISK_CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
        </Select>
        <button type="button" aria-pressed={mine} onClick={() => update({ mine: mine ? "" : "1" })} data-testid="risks-mine"
          className={`rounded-full px-3 py-1.5 text-sm font-medium ${mine ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>Les miens</button>
        <button type="button" aria-pressed={toReview} onClick={() => update({ review: toReview ? "" : "1" })} data-testid="risks-to-review"
          className={`rounded-full px-3 py-1.5 text-sm font-medium ${toReview ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>À revoir{data.counts?.to_review ? ` (${data.counts.to_review})` : ""}</button>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="risks-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem] items-start">
          <div className="space-y-3 min-w-0 xl:order-1">
            {items.length === 0 ? (
              <Card className="p-8 text-center space-y-2" data-testid="risks-empty">
                <ShieldAlert className="mx-auto text-slate-300" size={26} />
                {filtered ? <><h3 className="font-medium text-slate-900">Aucun risque ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></> : (
                  <><h3 className="font-medium text-slate-900">{EMPTY_TEXT[view][0]}</h3><p className="text-sm text-slate-500 max-w-md mx-auto">{EMPTY_TEXT[view][1]}</p>
                    {canManage && view !== "closed" && <Button asChild size="sm"><Link to="/gouvernance/risques/nouveau"><Plus size={14} className="mr-1.5" />Nouveau risque</Link></Button>}</>
                )}
              </Card>
            ) : (
              <>
                <Card className="hidden md:block overflow-hidden" data-testid="risks-table">
                  <Table>
                    <TableHeader><TableRow><TableHead>Risque</TableHead><TableHead>Niveau</TableHead><TableHead>Propriétaire</TableHead><TableHead className="hidden lg:table-cell">Prochaine revue</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {items.map((r) => (
                        <TableRow key={r.id} className="cursor-pointer" onClick={() => open(r)} data-testid={`risk-row-${r.number}`}>
                          <TableCell className="max-w-md"><div className="font-medium text-slate-900 truncate">{r.title}</div><div className="text-xs text-slate-500 tabular-nums">{r.number} · {r.category_label}</div></TableCell>
                          <TableCell><StateChips r={r} testid={`risk-level-${r.number}`} /></TableCell>
                          <TableCell className="text-sm">{r.owner.name || "—"}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-slate-600">{r.status === "open" ? formatDay(r.next_review_on) : <span className="text-slate-400">—</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
                <Card className="md:hidden divide-y divide-slate-100" data-testid="risks-cards">
                  {items.map((r) => (
                    <button key={r.id} type="button" onClick={() => open(r)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`risk-card-${r.number}`}>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="font-medium text-slate-900">{r.title}</div>
                        <div className="text-xs text-slate-500">{r.number} · {r.category_label}</div>
                        <StateChips r={r} testid={`risk-card-level-${r.number}`} />
                        <div className="text-xs text-slate-600">Propriétaire : {r.owner.name || "—"}</div>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
                    </button>
                  ))}
                </Card>
                <p className="text-xs text-slate-500" data-testid="risks-total">{items.length} risque{items.length > 1 ? "s" : ""}</p>
              </>
            )}
          </div>
          <div className="xl:order-2"><RiskGrid matrix={data.matrix} picked={cell} onPick={setCell} /></div>
        </div>
      )}
    </div>
  );
}
